/**
 * Sidebar fijo con menú agrupado por módulo.
 */
import { Link, useLocation } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { useState } from 'react';
import { menuSections } from '@/config/menu';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/utils/cn';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Sales Agent';
const SIDEBAR_WIDTH = 260;
const COLLAPSED_WIDTH = 64;

export function Sidebar() {
  const location = useLocation();
  const { hasPermission } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const width = collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const renderItem = (item: typeof menuSections[0]['items'][0]) => {
    if (item.permissionCode && !hasPermission(item.permissionCode)) return null;
    const active = isActive(item.path);
    const link = (
      <Link
        to={item.path}
        aria-current={active ? 'page' : undefined}
        onClick={() => setMobileOpen(false)}
        className={cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          active ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-200/50 hover:text-slate-900',
        )}
      >
        <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
        {!collapsed && <span>{item.label}</span>}
        {active && !collapsed && <span className="absolute left-0 h-5 w-[3px] rounded-r bg-white" />}
      </Link>
    );
    return (
      <li key={item.key} className="relative">
        {collapsed ? (
          <Tooltip title={item.label} placement="right">
            <span>{link}</span>
          </Tooltip>
        ) : (
          link
        )}
      </li>
    );
  };

  return (
    <>
      {/* Mobile toggle */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="fixed left-3 top-3 z-30 inline-flex h-10 w-10 items-center justify-center rounded-md bg-white shadow-sm lg:hidden"
        aria-label="Abrir menú"
      >
        <span className="material-symbols-outlined">menu</span>
      </button>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        role="navigation"
        aria-label="Menú principal"
        className={cn(
          'fixed left-0 top-0 z-50 flex h-screen flex-col border-r border-slate-200 bg-slate-50 transition-all duration-300 ease-in-out',
          collapsed ? 'lg:w-16' : 'lg:w-[260px]',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0',
        )}
        style={{ width: mobileOpen ? SIDEBAR_WIDTH : width }}
      >
        <div className="flex h-[60px] items-center justify-between border-b border-slate-200 px-3">
          {!collapsed && (
            <Link to="/app/dashboard" className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <span className="material-symbols-outlined text-primary">apps</span>
              <span className="truncate">{APP_NAME}</span>
            </Link>
          )}
          <button
            type="button"
            onClick={() => {
              setCollapsed((c) => !c);
              setMobileOpen(false);
            }}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-200"
            aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            <span className="material-symbols-outlined text-[18px]">
              {collapsed ? 'chevron_right' : 'chevron_left'}
            </span>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 sidebar-scrollbar">
          {menuSections.map((section) => {
            const visibleItems = section.items.filter((i) => !i.permissionCode || hasPermission(i.permissionCode));
            if (visibleItems.length === 0) return null;
            return (
              <div key={section.module} className="mb-4">
                {!collapsed && (
                  <h3 className="mb-1 px-3 text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">
                    {section.title}
                  </h3>
                )}
                <ul className="space-y-0.5">{visibleItems.map(renderItem)}</ul>
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
